import React, { VFC } from 'react';
import styled from 'styled-components';
import {
    Container,
    Row,
  Col,
    Visible,
    useScreenClass,
} from 'react-grid-system';
import { useAppNavigate } from '../hooks/useAppNavigate';
import Spacing from '../components/atoms/Spacing';
import Typography from '../components/atoms/Typography';
import { useReturnContext } from '../contexts/returnContext';
import ReturnListItem from '../components/organisms/ReturnListItem';
import TableItem from '../components/molecules/TableItem';
import PrimaryButton from '../components/molecules/PrimaryButton';
import { EASYCOM_ROUTES } from '../navigation';
import { useSettings } from '../contexts/settingsContext';
import MissingData from '../components/molecules/MissingData';
import Divider from '../components/atoms/Divider';

interface Props {
  radius?: string;
}

const SurroundingBorder = styled(Container)<Props>`
  border-radius: ${({ radius }) => radius};
  border: 1px solid #e0e0e0;
`;

const GrayBackground = styled(Row)<Props>`
  background-color: #fafafa;
  border-bottom-right-radius: ${({ radius }) => radius};
  border-bottom-left-radius: ${({ radius }) => radius};
`;

const ReturnBasketPage: VFC = () => {
  const { returns } = useReturnContext();
  const { cornerRadius } = useSettings();
  const navigate = useAppNavigate();
  const screenClass = useScreenClass();

  const sumOfProducts = Object.keys(returns).reduce(
    (previousValue, currentValue) =>
      previousValue + returns[currentValue].returnData.amount,
    0,
  );

  function onlyUnique(value, index, self) {
    return self.indexOf(value) === index;
  }

  const allOrderNos = Object.keys(returns).map(
    (key) => returns[key].orderNumber,
  );
  const sumOfOrders = allOrderNos.filter(onlyUnique).length;

  const table = [
    {
      key: 'sum_orders',
      title: <Typography translateKey="sum_orders" variant="body1-semi-bold" />,
      value: sumOfOrders,
    },
    {
      key: 'sum_products',
      title: (
        <Typography translateKey="sum_products" variant="body1-semi-bold" />
      ),
      value: sumOfProducts,
    },
  ];

  const textVariant = ['lg', 'xl', 'xxl'].includes(screenClass) ? 'h3' : 'h5';
  const alignLeft = ['sm', 'xs', 'md'].includes(screenClass) ? 'start' : 'end';

  return (
    <SurroundingBorder fluid radius={cornerRadius}>
      <Col style={{ backgroundColor: 'white' }}>
        <Spacing spacing={3} />
        <Row nogutter justify="center">
          <Typography
            translateKey="return_basket_title"
            translateParams={{ counter: Object.keys(returns).length }}
            variant={textVariant}
          />
        </Row>
        <Spacing spacing={3} />
      </Col>

      <Row>
        <Divider />
      </Row>

      <Container>
        <Spacing spacing={3} />
        <Row>
          {table.map(({ title, value, key }) => (
            <Col key={key} xs={12} sm={12} md={12} lg="content">
              <Row>
                <TableItem title={title} value={value} />
              </Row>
              <Visible xs sm md lg>
                <Spacing spacing={3} />
              </Visible>
            </Col>
          ))}
          <Col>
            <Row justify={alignLeft}>
              {Object.keys(returns).length > 0 && (
                <PrimaryButton
                  translateKey="choose_freight_option"
                  icon="arrow-right"
                  iconColor="white"
                  onClick={() => navigate(EASYCOM_ROUTES.RETURNS_FREIGHT)}
                />
              )}
            </Row>
          </Col>
        </Row>
        <Spacing spacing={3} />
      </Container>
      <Row>
        <Divider />
      </Row>

      <GrayBackground radius={cornerRadius}>
        {returns?.length === 0 ? (
          <MissingData translationKey="no_returns" />
        ) : (
          <Col>
            {Object.values(returns).map((item) => (
              <React.Fragment key={`${item.uid}-${item.returnData.reason}`}>
                <Spacing spacing={3} />
                <ReturnListItem {...item} />
              </React.Fragment>
            ))}
            <Spacing spacing={3} />
          </Col>
        )}
      </GrayBackground>
    </SurroundingBorder>
  );
};

export default ReturnBasketPage;
